import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import worldAtlas from 'world-atlas/countries-110m.json';
import { Card, Spinner, Alert } from '../../components/UI';
import { api, getUser } from '../../utils/api';
import MemberModal, { MembershipSwitch, useMembershipToggle } from '../community/MemberModal';

// Where we are — the community on a world map, by the country on their Microsoft profile
// (BC_Users.Country/City, filled by the Graph sync). Click a country → who works there
// and what time it is there. Click a person → their card (MemberModal).
// Superadmin also sees people removed from Basecamp (faded) and can switch membership here.

// Graph "country" is free text (name, local name or ISO code) → the atlas country name.
const ALIASES = {
  'united states': 'United States of America', usa: 'United States of America', us: 'United States of America',
  uk: 'United Kingdom', gb: 'United Kingdom', 'great britain': 'United Kingdom', england: 'United Kingdom',
  'bosnia and herzegovina': 'Bosnia and Herz.', 'bosna i hercegovina': 'Bosnia and Herz.', ba: 'Bosnia and Herz.', bih: 'Bosnia and Herz.',
  'north macedonia': 'Macedonia', 'severna makedonija': 'Macedonia', mk: 'Macedonia',
  'czech republic': 'Czechia', 'česko': 'Czechia', cz: 'Czechia',
  'ivory coast': "Côte d'Ivoire", 'congo (democratic republic)': 'Dem. Rep. Congo', 'dominican republic': 'Dominican Rep.',
  'central african republic': 'Central African Rep.', 'equatorial guinea': 'Eq. Guinea', eswatini: 'eSwatini',
  'south sudan': 'S. Sudan', 'solomon islands': 'Solomon Is.',
  srbija: 'Serbia', rs: 'Serbia', hrvatska: 'Croatia', hr: 'Croatia', slovenija: 'Slovenia', si: 'Slovenia',
  'crna gora': 'Montenegro', me: 'Montenegro', deutschland: 'Germany', de: 'Germany', österreich: 'Austria', at: 'Austria',
  schweiz: 'Switzerland', suisse: 'Switzerland', ch: 'Switzerland', españa: 'Spain', es: 'Spain', italia: 'Italy', it: 'Italy',
  polska: 'Poland', pl: 'Poland', magyarország: 'Hungary', hu: 'Hungary', românia: 'Romania', ro: 'Romania',
  slovensko: 'Slovakia', sk: 'Slovakia', 'българия': 'Bulgaria', bg: 'Bulgaria', 'ελλάδα': 'Greece', gr: 'Greece',
  türkiye: 'Turkey', tr: 'Turkey', fr: 'France', nl: 'Netherlands', nederland: 'Netherlands', be: 'Belgium',
  dk: 'Denmark', se: 'Sweden', no: 'Norway', fi: 'Finland', ie: 'Ireland', pt: 'Portugal', ua: 'Ukraine',
  al: 'Albania', xk: 'Kosovo', ae: 'United Arab Emirates', uae: 'United Arab Emirates', in: 'India', cn: 'China',
  au: 'Australia', ca: 'Canada', br: 'Brazil', mx: 'Mexico', za: 'South Africa', jp: 'Japan', kr: 'South Korea',
  ru: 'Russia', lt: 'Lithuania', lv: 'Latvia', ee: 'Estonia', cy: 'Cyprus', lu: 'Luxembourg',
};
const ATLAS_NAMES = new Set(worldAtlas.objects.countries.geometries.map(g => g.properties.name));
function toAtlasName(raw) {
  const t = String(raw || '').trim();
  if (!t) return null;
  const k = t.toLowerCase();
  if (ALIASES[k]) return ALIASES[k];
  for (const n of ATLAS_NAMES) if (n.toLowerCase() === k) return n;
  return t; // unknown to the atlas (e.g. Malta, Singapore) → still listed, just not drawn
}

// One representative time zone per country (capital) — enough for "what time is it there".
const TZ = {
  Serbia: 'Europe/Belgrade', Croatia: 'Europe/Zagreb', Slovenia: 'Europe/Ljubljana', 'Bosnia and Herz.': 'Europe/Sarajevo',
  Montenegro: 'Europe/Podgorica', Macedonia: 'Europe/Skopje', Kosovo: 'Europe/Belgrade', Albania: 'Europe/Tirane',
  Germany: 'Europe/Berlin', Austria: 'Europe/Vienna', Switzerland: 'Europe/Zurich', Italy: 'Europe/Rome', Spain: 'Europe/Madrid',
  Portugal: 'Europe/Lisbon', France: 'Europe/Paris', Belgium: 'Europe/Brussels', Netherlands: 'Europe/Amsterdam',
  Luxembourg: 'Europe/Luxembourg', 'United Kingdom': 'Europe/London', Ireland: 'Europe/Dublin', Denmark: 'Europe/Copenhagen',
  Sweden: 'Europe/Stockholm', Norway: 'Europe/Oslo', Finland: 'Europe/Helsinki', Iceland: 'Atlantic/Reykjavik',
  Poland: 'Europe/Warsaw', Czechia: 'Europe/Prague', Slovakia: 'Europe/Bratislava', Hungary: 'Europe/Budapest',
  Romania: 'Europe/Bucharest', Bulgaria: 'Europe/Sofia', Greece: 'Europe/Athens', Turkey: 'Europe/Istanbul', Cyprus: 'Asia/Nicosia',
  Ukraine: 'Europe/Kyiv', Moldova: 'Europe/Chisinau', Belarus: 'Europe/Minsk', Lithuania: 'Europe/Vilnius', Latvia: 'Europe/Riga',
  Estonia: 'Europe/Tallinn', Russia: 'Europe/Moscow', Georgia: 'Asia/Tbilisi', Armenia: 'Asia/Yerevan', Azerbaijan: 'Asia/Baku',
  Israel: 'Asia/Jerusalem', 'United Arab Emirates': 'Asia/Dubai', 'Saudi Arabia': 'Asia/Riyadh', Qatar: 'Asia/Qatar',
  Egypt: 'Africa/Cairo', Morocco: 'Africa/Casablanca', Tunisia: 'Africa/Tunis', Nigeria: 'Africa/Lagos', Kenya: 'Africa/Nairobi',
  'South Africa': 'Africa/Johannesburg', India: 'Asia/Kolkata', Pakistan: 'Asia/Karachi', China: 'Asia/Shanghai',
  Japan: 'Asia/Tokyo', 'South Korea': 'Asia/Seoul', Taiwan: 'Asia/Taipei', Vietnam: 'Asia/Ho_Chi_Minh', Thailand: 'Asia/Bangkok',
  Malaysia: 'Asia/Kuala_Lumpur', Indonesia: 'Asia/Jakarta', Philippines: 'Asia/Manila', Australia: 'Australia/Sydney',
  'New Zealand': 'Pacific/Auckland', 'United States of America': 'America/New_York', Canada: 'America/Toronto',
  Mexico: 'America/Mexico_City', Brazil: 'America/Sao_Paulo', Argentina: 'America/Argentina/Buenos_Aires', Chile: 'America/Santiago',
  Colombia: 'America/Bogota', Peru: 'America/Lima', Kazakhstan: 'Asia/Almaty', Malta: 'Europe/Malta', Singapore: 'Asia/Singapore',
  Liechtenstein: 'Europe/Vaduz', Monaco: 'Europe/Monaco', 'San Marino': 'Europe/San_Marino', Andorra: 'Europe/Andorra',
};

function localTime(country, now) {
  const tz = TZ[country];
  if (!tz) return null;
  try { return new Date(now).toLocaleTimeString('en-GB', { timeZone: tz, hour: '2-digit', minute: '2-digit' }); } catch { return null; }
}

function PersonRow({ m, onOpen, isAdmin, onToggleMember, busy }) {
  const [url, setUrl] = useState(null);
  useEffect(() => { let on = true; api.getMemberPhotoURL(m.id).then(u => { if (on) setUrl(u); }); return () => { on = false; }; }, [m.id]);
  const box = { width: 32, height: 32, borderRadius: '50%', flexShrink: 0, objectFit: 'cover', background: 'var(--ink)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-display)', fontSize: '0.85rem' };
  const removed = m.isMember === false;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: '1px solid var(--line)' }}>
      <button onClick={() => onOpen(m)} style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0, background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', fontFamily: 'var(--font-body)', color: 'var(--ink)', opacity: removed ? 0.5 : 1 }}>
        {url ? <img src={url} alt="" style={box} /> : <div style={box}>{(m.name || '?').charAt(0)}</div>}
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: '0.86rem', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.name}</div>
          <div style={{ fontSize: '0.74rem', color: 'var(--ink-faint)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{removed ? 'Not in Basecamp' : [m.city, m.jobTitle].filter(Boolean).join(' · ') || (m.roles || []).join(', ')}</div>
        </div>
      </button>
      {isAdmin && <MembershipSwitch on={!removed} busy={busy} onToggle={() => onToggleMember(m)} compact />}
    </div>
  );
}

const LENS_R = 85;      // lens radius, px
const LENS_ZOOM = 3;    // magnification

// The world map SVG. Memoized: drawn as the base map and again inside the lens.
const WorldSvg = memo(function WorldSvg({ byCountry, sel, onHover, onPick, stroke = 0.4 }) {
  const memberCount = (name) => (byCountry[name] || []).filter(m => m.isMember !== false).length;
  return (
    <ComposableMap projection="geoEqualEarth" projectionConfig={{ scale: 160, rotate: [-15, 0, 0], center: [0, 25] }} width={800} height={400} style={{ width: '100%', height: 'auto', display: 'block' }}>
      <Geographies geography={worldAtlas}>
        {({ geographies }) => geographies
          .filter(g => g.properties.name !== 'Antarctica')
          .map(geo => {
            const name = geo.properties.name;
            const count = memberCount(name);
            const listed = byCountry[name]?.length || 0; // incl. removed people (superadmin only)
            const isSel = sel === name;
            const fill = isSel ? 'var(--accent, #c8102e)' : count ? 'var(--ink)' : 'var(--canvas-warm)';
            return (
              <Geography
                key={geo.rsmKey}
                geography={geo}
                onMouseEnter={() => onHover({ name, count })}
                onClick={() => listed && onPick(name)}
                style={{
                  default: { fill, stroke: 'var(--canvas-white, #fff)', strokeWidth: stroke, outline: 'none' },
                  hover: { fill: count ? (isSel ? fill : 'var(--ink-soft)') : '#ddd5c6', stroke: 'var(--canvas-white, #fff)', strokeWidth: stroke, outline: 'none', cursor: listed ? 'pointer' : 'crosshair' },
                  pressed: { fill, outline: 'none' },
                }}
              />
            );
          })}
      </Geographies>
    </ComposableMap>
  );
});

export default function TeamWorldMap() {
  const [members, setMembers] = useState(null);
  const [selected, setSelected] = useState(null);
  const [hover, setHover] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const [lens, setLens] = useState(null); // { x, y, w } in px inside the map box
  const [open, setOpen] = useState(null); // person whose card is open
  const isAdmin = (getUser()?.roles || []).includes('SUPERADMIN');

  // Stable callbacks → the memoized maps don't re-render on every mouse move.
  const onHover = useCallback((h) => setHover(h), []);
  const onPick = useCallback((name) => setSelected(prev => (prev === name ? null : name)), []);
  const onPointerMove = useCallback((e) => {
    if (e.pointerType !== 'mouse') return; // touch: no hover, no lens
    const r = e.currentTarget.getBoundingClientRect();
    setLens({ x: e.clientX - r.left, y: e.clientY - r.top, w: r.width });
  }, []);

  const load = useCallback(() => {
    api.getMembers(isAdmin).then(d => setMembers(d.members || [])).catch(() => setMembers([]));
  }, [isAdmin]);
  useEffect(() => { load(); }, [load]);

  const [memberError, setMemberError] = useState(null);
  const membership = useMembershipToggle({
    onChanged: (m, next) => { setMemberError(null); setMembers(prev => prev.map(x => x.id === m.id ? { ...x, isMember: next } : x)); },
    onError: setMemberError,
  });
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 30000); return () => clearInterval(t); }, []);

  // Everyone in Basecamp (no candidates; superadmin also gets removed people), grouped by atlas country name.
  const byCountry = useMemo(() => {
    const map = {};
    (members || []).forEach(m => {
      const c = toAtlasName(m.country);
      if (!c) return;
      (map[c] = map[c] || []).push(m);
    });
    return map;
  }, [members]);
  const countries = Object.keys(byCountry).sort((a, b) => byCountry[b].length - byCountry[a].length || a.localeCompare(b));
  const offMap = countries.filter(c => !ATLAS_NAMES.has(c)); // too small for the 110m atlas
  const membersIn = (c) => byCountry[c].filter(m => m.isMember !== false).length;
  const people = countries.reduce((n, c) => n + membersIn(c), 0);
  const sel = selected && byCountry[selected] ? selected : null;

  return (
    <Card style={{ padding: '18px 20px', marginBottom: 22 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', margin: 0 }}>Where we are</h3>
        {members && <span style={{ fontSize: '0.8rem', color: 'var(--ink-faint)' }}>{people} people · {countries.length} countries — click a country</span>}
      </div>

      {!members ? <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><Spinner /></div> : (
        <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'stretch' }}>
          <div
            onPointerMove={onPointerMove}
            onPointerLeave={() => { setLens(null); setHover(null); }}
            style={{ flex: '3 1 420px', minWidth: 0, position: 'relative', background: 'var(--canvas)', borderRadius: 'var(--radius-md)', overflow: 'hidden', cursor: lens ? 'crosshair' : 'default' }}>
            <WorldSvg byCountry={byCountry} sel={sel} onHover={onHover} onPick={onPick} />
            {/* Magnifier — follows the mouse, shows that spot LENS_ZOOM× bigger; small countries are clickable inside it */}
            {lens && (
              <div style={{
                position: 'absolute', left: lens.x - LENS_R, top: lens.y - LENS_R, width: LENS_R * 2, height: LENS_R * 2,
                borderRadius: '50%', overflow: 'hidden', border: '2px solid var(--ink)', boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
                background: 'var(--canvas)', zIndex: 2,
              }}>
                <div style={{ position: 'absolute', width: lens.w * LENS_ZOOM, left: LENS_R - lens.x * LENS_ZOOM, top: LENS_R - lens.y * LENS_ZOOM }}>
                  <WorldSvg byCountry={byCountry} sel={sel} onHover={onHover} onPick={onPick} stroke={0.25} />
                </div>
              </div>
            )}
            {hover && (
              <div style={{ position: 'absolute', left: 10, top: 10, zIndex: 3, background: 'var(--canvas-white)', border: '1px solid var(--line)', borderRadius: 'var(--radius-sm)', padding: '4px 10px', fontSize: '0.78rem', pointerEvents: 'none' }}>
                <b>{hover.name}</b>{hover.count ? ` · ${hover.count} ${hover.count > 1 ? 'people' : 'person'}` : ''}{hover.count && localTime(hover.name, now) ? ` · ${localTime(hover.name, now)}` : ''}
              </div>
            )}
          </div>

          {/* Side panel — the map sets the height; the panel fills it and its list scrolls */}
          <div style={{ flex: '1 1 220px', minWidth: 220, minHeight: 260, position: 'relative' }}>
           <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column' }}>
            {sel ? (
              <>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.05rem' }}>{sel}</div>
                  <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-faint)', fontSize: '0.8rem' }}>✕</button>
                </div>
                {localTime(sel, now) && <div style={{ fontSize: '0.8rem', color: 'var(--ink-soft)', marginBottom: 8 }}>🕒 {localTime(sel, now)} local time</div>}
                <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>{memberError && <div style={{ margin: '6px 0' }}><Alert type="error">{memberError}</Alert></div>}{byCountry[sel].map(m => <PersonRow key={m.id} m={m} onOpen={setOpen} isAdmin={isAdmin} onToggleMember={membership.toggle} busy={membership.busyId === m.id} />)}</div>
              </>
            ) : (
              <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4, paddingRight: 4 }}>
                {countries.length === 0 && <div style={{ fontSize: '0.84rem', color: 'var(--ink-faint)' }}>No one has a country on their profile yet — run “Sync from Microsoft” in User Management.</div>}
                {countries.map(c => (
                  <button key={c} onClick={() => setSelected(c)} style={{ display: 'flex', flexShrink: 0, justifyContent: 'space-between', gap: 8, background: 'none', border: 'none', borderBottom: '1px solid var(--line)', padding: '6px 2px', cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: '0.84rem', color: 'var(--ink)', textAlign: 'left' }}>
                    <span>{c}{offMap.includes(c) ? <span style={{ color: 'var(--ink-faint)', fontSize: '0.7rem' }}> · not on map</span> : null}</span>
                    <span style={{ color: 'var(--ink-faint)' }}>{membersIn(c)}{localTime(c, now) ? ` · ${localTime(c, now)}` : ''}</span>
                  </button>
                ))}
              </div>
            )}
           </div>
          </div>
        </div>
      )}
      {open && <MemberModal member={open} onClose={() => setOpen(null)} onChanged={load} />}
      {membership.dialog}
    </Card>
  );
}
